import { Link } from "react-router-dom";
import BackToDocumentary from "../../shared/BackToDocumentary/BackToDocumentary";
import { products } from "../../../content";
import ProductOutcomes from "./ProductOutcomes";
import { splitProductName } from "../../../utils/productName";
import type { Product } from "../../../types/content";

/**
 * ============================================================================
 * Products — the /products route.
 * ============================================================================
 *
 * The owned SFT product suite. Direct consumer of the content layer, no bridge
 * (V.4), on the Stories / Martial Arts precedent. To change what this page
 * says, edit `src/content/data/products.json` — not this file.
 *
 * SPLIT FROM THE OLD PLATFORMS SECTION ON 2026-08-20 (D-067). That section
 * carried all 28 rows behind a category filter, which meant the nine owned
 * products and nineteen client builds competed for the same grid and the same
 * heading. They are different claims: one is "I own this product", the other is
 * "I delivered this for a client".
 *
 * NO CATEGORY FILTER HERE, DELIBERATELY. Every product is enterprise SaaS — the
 * category was the discriminator that corroborated the split — so a filter would
 * offer exactly one option beside "All". `Project` keeps its filter because four
 * categories are genuinely in play there.
 *
 * THE FIELD IS `involvement`, NOT `role`. It holds "Technical Committee" and
 * "Workflow Enhancement" beside "Product Owner", and the card labels it
 * "Involvement" by owner ruling. The data field was renamed to match the label
 * rather than left as `role` with a different word on screen, because a display
 * string that diverges from its field is exactly how D-065 happened.
 */

/**
 * The page's standfirst — used verbatim as this route's meta description
 * (D-044), so the search result cannot drift from the page's opening line.
 *
 * It asserts no count and no span: those belong to the layer, which computes
 * them (D-066 — a standfirst ships in the meta description and is the one line
 * on a page that nothing checks).
 */
export const PRODUCTS_STANDFIRST =
	"The owned product suite at Shyam Future Tech — multi-tenant enterprise SaaS built and governed in-house, each with the involvement it was carried under and the outcomes it produced.";

/**
 * ON THIS PAGE THE PARENTHETICAL IS THE HEADLINE, NOT THE QUALIFIER. A reader
 * on /products is looking for "SFT Vault", not "Enterprise Asset Management
 * Platform"; the descriptive half becomes the sub-line. Entries with no
 * parenthetical keep their name as the heading.
 *
 * The split itself lives in `utils/productName.ts` — it had been written here
 * and again in `ProductsTeaser.tsx`, and a third consumer is what made two
 * copies of one string format worth removing rather than noting (D-061).
 */
const ProductCard = ({ product }: { product: Product }): JSX.Element => {
	const { brand, descriptor } = splitProductName(product.name);

	return (
		/* `card-shell` / `card-face` — the site-wide card standard (D-061).

		   THE `id` IS THE LANDING SITE FOR THE HOME TEASER (D-099). Each teaser
		   card links to `/products#<id>`, and `ScrollManager` resolves a hash on
		   a client-side route change by `getElementById` — so without this
		   attribute the link would navigate correctly and then sit at the top of
		   the page, which looks like a broken link rather than a missing id.
		   `scroll-mt-24` keeps the card clear of the fixed navbar; the id goes on
		   the OUTER element so the whole card is the target, which is the call
		   `Stories.tsx` already makes for its 23 anchored entries. */
		<div id={product.id} className="card-shell scroll-mt-24">
			<article className="card-face flex h-full flex-col p-6">
				{/* The same brand-ramp hairline the teaser card carries, so a reader
				    arriving from Home lands on something that looks like what they
				    clicked. Decorative, hence the brand ramp rather than a `--cat-*`
				    hue — see the note in Services. */}
				<span
					aria-hidden="true"
					className="mb-5 block h-[3px] w-10 rounded-full bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]"
				/>

				<h2 className="text-xl font-bold text-[var(--text-strong)]">{brand}</h2>
				{descriptor && <p className="mt-1 text-sm font-medium text-[var(--text-secondary)]">{descriptor}</p>}

				{/* "Involvement", not "Role" — owner ruling 2026-08-20. The values
				    include contribution types ("Technical Committee") that read
				    wrong under a Role label. */}
				<p className="mt-3 text-sm text-[var(--text-muted)]">
					<span className="font-semibold uppercase tracking-wide text-[var(--text-muted)]">Involvement</span>
					{" · "}
					<span className="font-medium text-[var(--text-primary)]">{product.involvement}</span>
				</p>

				<p className="mt-4 text-sm leading-relaxed text-[var(--text-secondary)]">{product.description}</p>

				{/* The rail is shared with the Home teaser's expandable panel
				    (D-075). Same records, same markup, one definition — the
				    guard against an empty list moved into it with the markup. */}
				<ProductOutcomes outcomes={product.outcomes} className="mt-5" />

				{/* Pushed to the foot so cards in a row align on their tag rail. */}
				<div className="mt-auto flex flex-wrap gap-2 pt-5">
					{product.tags.map((tag: string) => (
						<span key={tag} className="chip-quiet">
							{tag}
						</span>
					))}
				</div>
			</article>
		</div>
	);
};

export default function Products(): JSX.Element {
	return (
		<section className="section-shell" aria-labelledby="products-heading">
			<div className="section-rail">
				<p className="section-eyebrow">Owned Product Suite</p>

				{/* THE ROUTE'S ONLY <h1>. Every route ships exactly one (D-042). */}
				<h1 id="products-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Products
					</span>
				</h1>

				<p className="section-standfirst">{PRODUCTS_STANDFIRST}</p>

				{/* `auto-rows-fr` for the same reason Services gained it (D-099):
				    a grid row stretches its items to the tallest of THAT row, so
				    cards matched their row-mates and not the rows above or below.
				    Nine products divide evenly into three columns, so there is no
				    lone card here and `grid-balanced` is not needed — added where
				    it is required rather than everywhere it might one day be. */}
				<div className="mt-12 grid w-full auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 xl:grid-cols-3 xl:gap-8">
					{products.map((product: Product) => (
						<ProductCard key={product.id} product={product} />
					))}
				</div>

				<div className="mt-16 flex flex-wrap justify-center gap-6">
					<Link
						to="/projects"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						Client projects →
					</Link>
					<BackToDocumentary />
				</div>
			</div>
		</section>
	);
}
